import { createAsyncThunk } from '@reduxjs/toolkit';

import ApiClient from 'api/axiosClient';
import { buildQueryString } from 'utils';
import { notifyError, notifySuccess } from 'utils/notifyConfig';
import sessionManager from 'utils/sessionManager';

// Lead Sources
export const getLeadSources = createAsyncThunk(
  'sales/lead-sources/get',
  async (params, { rejectWithValue }) => {
    const query = buildQueryString(params);
    try {
      const response = await ApiClient.get(`/sales/lead-sources?${query}`);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const createLeadSource = createAsyncThunk(
  'sales/lead-sources/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post('/sales/lead-sources', data);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const deleteLeadSource = createAsyncThunk(
  'sales/lead-sources/delete',
  async (uuid, { rejectWithValue }) => {
    try {
      const response = await ApiClient.del(`/sales/lead-sources/${uuid}`);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const editLeadSource = createAsyncThunk(
  'sales/lead-sources/edit',
  async ({ uuid, body }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.put(`/sales/lead-sources/${uuid}`, body);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

// Sales Scripts
export const getSalesScripts = createAsyncThunk(
  'sales/scripts/get',
  async (params, { rejectWithValue }) => {
    const sessionParams = sessionManager.getQueryParams();
    const combinedParams = { ...params, ...sessionParams };
    const query = buildQueryString(combinedParams);
    try {
      const response = await ApiClient.get(`/sales/sales-scripts?${query}`);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const createSalesScript = createAsyncThunk(
  'sales/scripts/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post('/sales/sales-scripts', data);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const deleteSalesScript = createAsyncThunk(
  'sales/scripts/delete',
  async (uuid, { rejectWithValue }) => {
    try {
      const response = await ApiClient.del(`/sales/sales-scripts/${uuid}`);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const editSalesScript = createAsyncThunk(
  'sales/scripts/edit',
  async ({ uuid, body }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.put(`/sales/sales-scripts/${uuid}`, body);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

// Product
export const getProducts = createAsyncThunk(
  'sales/products/get',
  async (params, { rejectWithValue }) => {
    const sessionParams = sessionManager.getQueryParams();
    const combinedParams = { ...params, ...sessionParams };
    const query = buildQueryString(combinedParams);
    try {
      const response = await ApiClient.get(`/sales/products?${query}`);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const createProduct = createAsyncThunk(
  'sales/products/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post('/sales/products', data);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const deleteProduct = createAsyncThunk(
  'sales/products/delete',
  async (uuid, { rejectWithValue }) => {
    try {
      const response = await ApiClient.del(`/sales/products/${uuid}`);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const editProduct = createAsyncThunk(
  'sales/products/edit',
  async ({ uuid, body }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.put(`/sales/products/${uuid}`, body);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

// Offer
export const getOffers = createAsyncThunk(
  'sales/offers/get',
  async (params, { rejectWithValue }) => {
    const sessionParams = sessionManager.getQueryParams();
    const combinedParams = { ...params, ...sessionParams };
    const query = buildQueryString(combinedParams);
    try {
      const response = await ApiClient.get(`/sales/offers?${query}`);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const createOffer = createAsyncThunk(
  'sales/offers/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post('/sales/offers', data);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const deleteOffer = createAsyncThunk(
  'sales/offers/delete',
  async (uuid, { rejectWithValue }) => {
    try {
      const response = await ApiClient.del(`/sales/offers/${uuid}`);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const editOffer = createAsyncThunk(
  'sales/offers/edit',
  async ({ uuid, body }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.put(`/sales/offers/${uuid}`, body);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

// Workflow Status
export const getWorkflowStatuses = createAsyncThunk(
  'sales/workflow-statuses/get',
  async (params, { rejectWithValue }) => {
    const sessionParams = sessionManager.getQueryParams();
    const combinedParams = { ...params, ...sessionParams };
    const query = buildQueryString(combinedParams);
    try {
      const response = await ApiClient.get(`/sales/workflow-statuses?${query}`);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const getWorkflowStatusesByPrivilege = createAsyncThunk(
  'sales/workflow-statuses/by-privilege/get',
  async (params, { rejectWithValue }) => {
    const query = buildQueryString(params);
    try {
      const response = await ApiClient.get(
        `/sales/leads/workflow-statuses/by-privilege?${query}`
      );
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const createWorkflowStatus = createAsyncThunk(
  'sales/workflow-statuses/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post('/sales/workflow-statuses', data);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const deleteWorkflowStatus = createAsyncThunk(
  'sales/workflow-statuses/delete',
  async (uuid, { rejectWithValue }) => {
    try {
      const response = await ApiClient.del(`/sales/workflow-statuses/${uuid}`);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const editWorkflowStatus = createAsyncThunk(
  'sales/workflow-statuses/edit',
  async ({ uuid, body }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.put(`/sales/workflow-statuses/${uuid}`, body);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

// User Privilege
export const getUserPrivileges = createAsyncThunk(
  'sales/user-privileges/get',
  async (params, { rejectWithValue }) => {
    const sessionParams = sessionManager.getQueryParams();
    const combinedParams = { ...params, ...sessionParams };
    const query = buildQueryString(combinedParams);
    try {
      const response = await ApiClient.get(`/sales/user-privileges?${query}`);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const createUserPrivilege = createAsyncThunk(
  'sales/user-privileges/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post('/sales/user-privileges', data);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const deleteUserPrivilege = createAsyncThunk(
  'sales/user-privileges/delete',
  async (uuid, { rejectWithValue }) => {
    try {
      const response = await ApiClient.del(`/sales/user-privileges/${uuid}`);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const editUserPrivilege = createAsyncThunk(
  'sales/user-privileges/edit',
  async ({ uuid, body }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.put(`/sales/user-privileges/${uuid}`, body);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

// Import Lead

export const createImportLead = createAsyncThunk(
  'sales/import-leads',
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post('/sales/import-leads', data, {
        'Content-Type': 'multipart/form-data',
      });
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

// Subproject and Sources

export const getSubprojectSources = createAsyncThunk(
  '/subprojects/sources/get',
  async ({ uuid, query }, { rejectWithValue }) => {
    const q = buildQueryString(query);
    try {
      const response = await ApiClient.get(`/subprojects/${uuid}/sources?${q}`);
      return response;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const assignSubprojectSources = createAsyncThunk(
  '/subprojects/sources/assign',
  async ({ uuid, sourceIds }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post(`/subprojects/${uuid}/sources/assign`, {
        sourceIds,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const removeSubprojectSources = createAsyncThunk(
  '/subprojects/sources/remove',
  async ({ uuid, sourceIds }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post(`/subprojects/${uuid}/sources/remove`, {
        sourceIds,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Project and Offers

export const getProjectOffers = createAsyncThunk(
  '/projects/offers/get/asd',
  async ({ uuid, query }, { rejectWithValue }) => {
    const q = buildQueryString(query);
    try {
      const response = await ApiClient.get(`/projects/offers/${uuid}?${q}`);
      return response;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const assignProjectOffers = createAsyncThunk(
  '/projects/offers/assign',
  async ({ uuid, offerIds }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post(`/projects/offers/assign/${uuid}`, {
        offerIds,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const removeProjectOffers = createAsyncThunk(
  '/projects/offers/remove',
  async ({ uuid, offerIds }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post(`/projects/offers/remove/${uuid}`, {
        offerIds,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Sales Dashboard

export const getSalesProjects = createAsyncThunk(
  '/sales/dashboard/projects',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get('/sales/dashboard/projects');
      return response;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const createLeadB2B = createAsyncThunk(
  'sales/leads-b2b/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post('/sales/leads/b2b', data);
      notifySuccess('B2B added successfully');
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const getSalesSubproject = createAsyncThunk(
  '/sales/dashboard/projects/projectId',
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(`/sales/dashboard/projects/${projectId}`);
      return response;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const createLeadB2C = createAsyncThunk(
  'sales/leads-b2c/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post('/sales/leads/b2c', data);
      notifySuccess('B2C added successfully');
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const getSalesLeads = createAsyncThunk(
  '/sales/leads/subproject/subprojectId',
  async (params, { rejectWithValue }) => {
    const { subprojectId } = params;
    const sessionParams = sessionManager.getQueryParams();
    const combinedParams = { ...params, ...sessionParams };
    const query = buildQueryString(combinedParams);

    try {
      const response = await ApiClient.get(`/sales/leads/subproject/${subprojectId}?${query}`);
      return response;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const getLeadById = createAsyncThunk(
  'sales/leads/getById',
  async (leadId, { rejectWithValue }) => {
    try {
      const queryParams = sessionManager.getQueryParams();
      const queryString = buildQueryString(queryParams);
      const response = await ApiClient.get(`/sales/leads/${leadId}?${queryString}`);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      // Don't show notification for 409 conflicts - let the lock system handle it
      if (error.status !== 409) {
        notifyError(message);
      }
      return rejectWithValue(message);
    }
  }
);

export const addLeadNote = createAsyncThunk(
  'sales/leads/addNote',
  async ({ leadId, data }, { rejectWithValue }) => {
    try {
      const queryParams = sessionManager.getQueryParams();
      const queryString = buildQueryString(queryParams);
      const response = await ApiClient.post(
        `/sales/leads/${leadId}/notes?${queryString}`,
        data
      );
      notifySuccess('Note added successfully');
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const createLeadOrder = createAsyncThunk(
  'sales/leadOrder/create',
  async ({ leadId, data }, { rejectWithValue }) => {
    try {
      const queryParams = sessionManager.getQueryParams();
      const queryString = buildQueryString(queryParams);
      const response = await ApiClient.post(
        `/sales/leads/${leadId}/orders?${queryString}`,
        data
      );
      notifySuccess('Order added successfully');
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);
export const nextLeadStep = createAsyncThunk(
  'sales/lead/nextStep',
  async ({ leadId, data }, { rejectWithValue }) => {
    try {
      const queryParams = sessionManager.getQueryParams();
      const queryString = buildQueryString(queryParams);
      const response = await ApiClient.post(
        `/sales/leads/${leadId}/next?${queryString}`,
        data
      );
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);
export const updateLeadStatus = createAsyncThunk(
  'sales/lead/updateStatus',
  async ({ leadId, data }, { rejectWithValue }) => {
    try {
      const queryParams = sessionManager.getQueryParams();
      const queryString = buildQueryString(queryParams);
      const response = await ApiClient.post(
        `/sales/leads/${leadId}/status?${queryString}`,
        data
      );
      notifySuccess('Status changed successfully');
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const updateLeadB2BDetails = createAsyncThunk(
  'sales/lead/updateB2BDetails',
  async ({ leadId, data }, { rejectWithValue }) => {
    try {
      const queryParams = sessionManager.getQueryParams();
      const queryString = buildQueryString(queryParams);
      const response = await ApiClient.put(`/sales/leads/${leadId}/b2b?${queryString}`, data);
      notifySuccess('Lead details updated successfully.');
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const updateLeadB2CDetails = createAsyncThunk(
  'sales/lead/updateB2CDetails',
  async ({ leadId, data }, { rejectWithValue }) => {
    try {
      const queryParams = sessionManager.getQueryParams();
      const queryString = buildQueryString(queryParams);
      const response = await ApiClient.put(`/sales/leads/${leadId}/b2c?${queryString}`, data);
      notifySuccess('Lead details updated successfully.');
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const searchLeadByPhone = createAsyncThunk(
  'sales/lead/searchByPhone',
  async (phoneNumber, { rejectWithValue }) => {
    try {
      const queryParams = sessionManager.getQueryParams();
      const queryString = buildQueryString(queryParams);
      const response = await ApiClient.post(`/sales/leads/search-phone?${queryString}`, {
        phoneNumber,
      });
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);

export const getRandomLead = createAsyncThunk(
  'sales/lead/getRandomLead',
  async ({ params }, { rejectWithValue }) => {
    const sessionParams = sessionManager.getQueryParams();
    const combinedParams = { ...params, ...sessionParams };
    const query = buildQueryString(combinedParams);

    try {
      const response = await ApiClient.get(`/sales/leads/random?${query}`);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      notifyError(message);
      return rejectWithValue(message);
    }
  }
);
